
import React from "react";
import { CalendarDays, List, ClipboardCheck } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import EventCalendar from "@/components/EventCalendar";
import EventList from "@/components/EventList";
import { EventApprovalList } from "@/components/events/EventApprovalList";
import { Event } from "@/types";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

interface EventTabViewProps {
  view: "calendar" | "list" | "approvals";
  setView: (view: "calendar" | "list" | "approvals") => void;
  isLoading: boolean;
  error: Error | null;
  filteredEvents: Event[];
  refetch: () => void;
  isAdmin: boolean;
}

const EventTabView: React.FC<EventTabViewProps> = ({
  view,
  setView,
  isLoading,
  error,
  filteredEvents,
  refetch,
  isAdmin,
}) => {
  return (
    <Tabs defaultValue="calendar" className="w-full" onValueChange={(value) => setView(value as typeof view)}>
      <TabsList className="mb-6">
        <TabsTrigger value="calendar" className="flex items-center gap-2">
          <CalendarDays size={16} />
          Calendar View
        </TabsTrigger>
        <TabsTrigger value="list" className="flex items-center gap-2">
          <List size={16} />
          List View
        </TabsTrigger>
        {isAdmin && (
          <TabsTrigger value="approvals" className="flex items-center gap-2">
            <ClipboardCheck size={16} />
            Pending Approvals
          </TabsTrigger>
        )}
      </TabsList>
      
      {isLoading ? (
        <div className="space-y-4">
          {view === "calendar" ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <Skeleton className="h-[400px] w-full rounded-md" />
              </div>
              <Skeleton className="h-[400px] w-full rounded-md" />
            </div>
          ) : (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-40 w-full rounded-md" />
              ))}
            </div>
          )}
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <p className="text-red-500">Error loading events. Please try again later.</p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={() => refetch()}
          >
            Retry
          </Button>
        </div>
      ) : (
        <>
          <TabsContent value="calendar" className="mt-0">
            <EventCalendar events={filteredEvents || []} />
          </TabsContent>
          <TabsContent value="list" className="mt-0">
            <EventList events={filteredEvents || []} />
          </TabsContent>
          {isAdmin && (
            <TabsContent value="approvals" className="mt-0">
              <EventApprovalList />
            </TabsContent>
          )}
        </>
      )}
    </Tabs>
  );
};

export default EventTabView;
